<template>
    <p-modal name="nonMerchSalesModal"
             label="Non-Merch Sales"
             ref="nonMerchSalesModal"
             size="lg"
             @ok.prevent="handleModalOk">
        <template v-slot:default>
            <p-form ref="nonMerchSalesForm">
                <div class="container-fluid content-wrapper">
                    <b-row>
                        <b-col>
                            <p>Non-Merch sales subtotaled by item</p>
                        </b-col>
                    </b-row>
                    <b-row cols="1" cols-lg="2">
                        <b-col>
                            <p-card title="POS Devices" class="static-height">
                                <p-filter-select data-type="posDevicesWithLocation"
                                                 rules="required"
                                                 :default-selected="true"
                                                 :initial-display-count="10"
                                                 select-all-button
                                                 v-model="reportParameters.registerIds" />
                            </p-card>
                            <p-card title="Cashier(s)" class="static-height">
                                <p-filter-select data-type="nbcUsers"
                                                 rules="required"
                                                 :default-selected="true"
                                                 :initial-display-count="10"
                                                 select-all-button
                                                 v-model="reportParameters.nbcUserIds" />
                            </p-card>
                        </b-col>
                        <b-col>
                            <p-card title="Date Range">
                                <p-date-range-widget :date-range-options="reportParameters" />
                            </p-card>
                            <p-card title="Format">
                                <b-row>
                                    <b-col>
                                        <b-form-radio value="html" v-model="outputFormat">HTML</b-form-radio>
                                    </b-col>
                                </b-row>
                                <b-row class="pt-1">
                                    <b-col>
                                        <b-form-radio value="csv" v-model="outputFormat">CSV</b-form-radio>
                                    </b-col>
                                </b-row>
                            </p-card>
                        </b-col>
                    </b-row>
                    <UserReportTemplate :user-report-template-options="userReportTemplateOptions"
                                        v-model:selected-user-report-template-id="selectedUserReportTemplateId"
                                        :selected-user-report-template="selectedUserReportTemplate"
                                        v-model:template-name="templateName"
                                        @selected-object="applyUserReportTemplate($event)" />
                </div>
            </p-form>
        </template>
        <template v-slot:modal-footer="{ ok, cancel }">
            <b-row>
                <b-col>
                    <p-button variant="outline-primary"
                              :is-busy="isBusy"
                              @click="cancel">Cancel</p-button>
                    <p-button variant="primary"
                              :is-busy="isBusy"
                              class="ms-2"
                              @click="ok">OK</p-button>
                </b-col>
            </b-row>
        </template>
    </p-modal>
</template>

<script>
import axios from 'axios';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import { getExportMessage } from '@/components/Common/ExportMessage.js';
import pCard from '@/components/p-card.vue';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import ReportTemplates from '@/components/mixins/ReportTemplates.js';
import UserReportTemplate from '@/components/UserReportTemplate.vue';

export default {
    mixins: [ReportTemplates],
    components: { pCard, UserReportTemplate },
    data() {
        return {
            isBusy: false,
            outputFormat: 'html',
            // Identifies which report saved templates belong to; must match the report name in Reports.vue.
            reportName: 'Non-Merch Sales',
            reportParameters: {
                registerIds: [],
                nbcUserIds: [],
                startDate: null,
                endDate: null,
                dateOption: 0,
                locationId: null
            }
        };
    },
    methods: {
        resetForm() {
            this.outputFormat = 'html';
            this.reportParameters.dateOption = 0;
            this.reportParameters.startDate = new Date().toISOString();
            this.reportParameters.endDate = new Date().toISOString();
        },
        async show() {
            this.resetForm();

            const posDevices = await selectListOptionsDataContext.getStoreDropdownData('posDevicesWithLocation');
            this.reportParameters.registerIds = (posDevices || []).map(x => x.value);

            const nbcUsers = await selectListOptionsDataContext.getStoreDropdownData('nbcUsers');
            this.reportParameters.nbcUserIds = (nbcUsers || []).map(x => x.value);

            this.reportParameters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();

            await this.loadUserReportTemplates();

            this.$refs.nonMerchSalesModal.show();
        },
        // ReportTemplates hook: POS devices and cashiers saved into a template can be gone, or out of scope
        // for the current location, by the time it is loaded - check them against the lists the pickers offer.
        async validateReportParameters(savedParameters) {
            const posDevices = await selectListOptionsDataContext.getStoreDropdownData('posDevicesWithLocation');
            const availablePosDeviceIds = new Set((posDevices || []).map(posDevice => posDevice.value));

            const nbcUsers = await selectListOptionsDataContext.getStoreDropdownData('nbcUsers');
            const availableNbcUserIds = new Set((nbcUsers || []).map(nbcUser => nbcUser.value));

            return (savedParameters.registerIds ?? []).every(registerId => availablePosDeviceIds.has(registerId))
                && (savedParameters.nbcUserIds ?? []).every(nbcUserId => availableNbcUserIds.has(nbcUserId));
        },
        async handleModalOk() {
            if (!(await this.$refs.nonMerchSalesForm.validate())) {
                return;
            }

            if (this.reportParameters.registerIds.length === 0) {
                this.$appError('Please select one or more POS Devices.');
                return;
            }

            if (this.reportParameters.nbcUserIds.length === 0) {
                this.$appError('Please select one or more Cashiers.');
                return;
            }

            this.isBusy = true;

            try {
                // The current location always wins: a restored template carries the location that was
                // active when it was saved.
                this.reportParameters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();

                if (!(await this.saveUserReportTemplate())) {
                    return;
                }

                if (this.outputFormat === 'html') {
                    await this.openHtmlReport();
                }
                else {
                    await this.downloadCsvReport();
                }

                this.$refs.nonMerchSalesModal.hide();
            } finally {
                this.isBusy = false;
            }
        },
        async openHtmlReport() {
            const result = await axios.post('sales/exportNonMerchSalesHtml', this.reportParameters, { responseType: 'text' });

            // The tab opens only after the report comes back, which a long run can push past the browser's
            // pop-up allowance - say so rather than failing silently.
            const reportTab = window.open();

            if (!reportTab) {
                this.$appError('The report tab was blocked by the browser. Please allow pop-ups for this site and try again.');
                return;
            }

            reportTab.document.write(result.data);
            reportTab.document.close();

            this.$appSuccess(`${this.reportName} report generated successfully.`);
        },
        async downloadCsvReport() {
            const result = await axios.post('sales/exportNonMerchSalesCsv', this.reportParameters, { responseType: 'blob' });

            downloadFileToBrowser(result.data, `${this.reportName}.csv`, 'application/csv');

            // An empty export already raised the "No Non-Merch sales found." warning through the interceptor.
            if (!getExportMessage(result)) {
                this.$appSuccess(`${this.reportName} report generated successfully.`);
            }
        }
    }
};
</script>

<style scoped>
.static-height {
    height: 370px;
}
</style>
